import { apiClient } from './client'

/**
 * Audit Logs API Service
 *
 * Provides methods to interact with the read-only audit logs API
 */

/**
 * Get audit logs with filters and pagination
 *
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.per_page - Results per page (default: 50, max: 100)
 * @param {number} params.user_id - Filter by user ID
 * @param {number} params.company_id - Filter by company ID
 * @param {string} params.action - Filter by event code
 * @param {string} params.date_from - Start date (YYYY-MM-DD)
 * @param {string} params.date_to - End date (YYYY-MM-DD)
 * @param {string} params.search - Search in action/description
 * @returns {Promise<Object>} Audit logs data with pagination
 */
export const getAuditLogs = async (params = {}) => {
	const queryParams = new URLSearchParams()

	Object.keys(params).forEach((key) => {
		if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
			queryParams.append(key, params[key])
		}
	})

	const queryString = queryParams.toString()
	const endpoint = queryString ? `/audit-logs?${queryString}` : '/audit-logs'

	return apiClient.get(endpoint)
}

/**
 * Get a single audit log by ID
 *
 * @param {number} id - Audit log ID
 * @returns {Promise<Object>} Audit log data
 */
export const getAuditLogById = async (id) => {
	return apiClient.get(`/audit-logs/${id}`)
}

/**
 * Get all available event codes grouped by category
 *
 * @returns {Promise<Object>} Event codes by category
 */
export const getEventCodes = async () => {
	return apiClient.get('/audit-logs/event-codes')
}

/**
 * Get distinct action codes that are currently in use
 *
 * @returns {Promise<Array>} Array of action codes
 */
export const getDistinctActions = async () => {
	return apiClient.get('/audit-logs/distinct-actions')
}

/**
 * Get recent audit logs
 *
 * @param {number} limit - Number of logs to retrieve (default: 50, max: 100)
 * @returns {Promise<Object>} Recent audit logs
 */
export const getRecentLogs = async (limit = 50) => {
	return apiClient.get(`/audit-logs/recent?limit=${limit}`)
}

/**
 * Get audit logs for a specific user
 *
 * @param {number} userId - User ID
 * @param {number} limit - Number of logs to retrieve (default: 50, max: 100)
 * @returns {Promise<Object>} User's audit logs
 */
export const getLogsByUser = async (userId, limit = 50) => {
	return apiClient.get(`/audit-logs/user/${userId}?limit=${limit}`)
}

/**
 * Get audit logs for a specific company
 *
 * @param {number} companyId - Company ID
 * @param {number} limit - Number of logs to retrieve (default: 50, max: 100)
 * @returns {Promise<Object>} Company's audit logs
 */
export const getLogsByCompany = async (companyId, limit = 50) => {
	return apiClient.get(`/audit-logs/company/${companyId}?limit=${limit}`)
}

/**
 * Export audit logs to CSV
 * (Client-side implementation - formats current logs as CSV)
 *
 * @param {Array} logs - Array of log objects
 * @param {string} filename - Filename for export (default: audit-logs.csv)
 */
export const exportLogsToCSV = (logs, filename = 'audit-logs.csv') => {
	if (!logs || logs.length === 0) {
		throw new Error('No logs to export')
	}

	// CSV headers
	const headers = ['ID', 'Date/Time', 'User ID', 'Company ID', 'Action', 'Description', 'IP Address', 'User Agent']

	// Convert logs to CSV rows
	const rows = logs.map((log) => [
		log.id,
		log.created_at,
		log.user_id || 'N/A',
		log.company_id || 'N/A',
		log.action,
		`"${(log.description || '').replace(/"/g, '""')}"`, // Escape quotes in description
		log.ip_address || 'N/A',
		`"${(log.user_agent || '').replace(/"/g, '""')}"`, // Escape quotes in user agent
	])

	// Combine headers and rows
	const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n')

	// Create blob and download
	const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
	const link = document.createElement('a')
	const url = URL.createObjectURL(blob)

	link.setAttribute('href', url)
	link.setAttribute('download', filename)
	link.style.visibility = 'hidden'

	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
}
